import { InformationButton } from "@components/buttons/information";
import {
  SHAvatar,
  SHBox,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { StatusBadge } from "@components/status-badge";
import { DateTimeFormat } from "@constants/format";
import { FeatureTemplateListDTO } from "@models/feature-template/entities/featureTemplate";
import { FeatureTemplateStatus } from "@models/feature-template/enums/status";
import { TextOverflowEllipsis } from "@pages/platform-analysis/components/text-overflow-ellipsis";
import {
  FeatureTemplateFilterOptions,
  RateCardFilterOptions,
} from "@pages/platform-analysis/constant";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { nameOfFactory } from "@utils";
import { format } from "date-fns";

export const FeatureTemplateNameOfFactory =
  nameOfFactory<FeatureTemplateListDTO>();
export const FeatureTemplateColumnHelper =
  createColumnHelper<FeatureTemplateListDTO>();

export const getAnalysisFeatureTemplateTableColumnDefs = ({
  renderItemActions,
}: {
  renderItemActions: (itemData: FeatureTemplateListDTO) => React.ReactNode;
}): ColumnDef<FeatureTemplateListDTO, any>[] => {
  const columnHelper = FeatureTemplateColumnHelper;
  const cols: ColumnDef<FeatureTemplateListDTO, any>[] = [
    columnHelper.accessor((row) => row, {
      id: FeatureTemplateNameOfFactory("name"),
      header: "Template name",
      cell: (props) => {
        return (
          <SHStack direction={"row"}>
            <TextOverflowEllipsis
              fontWeight="bold"
              value={props.cell.getValue().name ?? ""}
            />
            <SHBox
              sx={{
                display: "inline-flex",
                alignItems: "flex-end",
              }}
            >
              {props.cell.getValue().notes ? (
                <InformationButton content={props.cell.getValue().notes} />
              ) : (
                ""
              )}
            </SHBox>
          </SHStack>
        );
      },
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "20%",
          minWidth: "250px",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor((row) => row, {
      header: "Owner",
      id: FeatureTemplateNameOfFactory("ownerName"),
      cell: (props) => {
        return (
          <SHStack direction={"row"} alignItems={"center"}>
            <SHAvatar
              width={"24px"}
              height={"24px"}
              src={props.cell.getValue().ownerAvatarUrl}
            />
            <TextOverflowEllipsis
              value={props.cell.getValue().ownerName ?? ""}
              sx={{
                marginLeft: "5px",
                minWidth: "100%",
              }}
            />
          </SHStack>
        );
      },
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "12%",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor("lastModifiedDate", {
      header: "Last edit",
      cell: (props) => {
        const date = props.cell.getValue();
        return date ? format(new Date(date), DateTimeFormat) : "";
      },
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "13%",
          minWidth: { xs: "130px" },
          padding: "0 0 0 25px !important",
        },
      },
    }),
    columnHelper.accessor((row) => row, {
      header: "Features selected",
      id: "selectedFeatures",
      cell: (props) => (
        <SHTypography fontSize={13} lineHeight={"120%"} minWidth={"100px"}>
          {props.cell.getValue().selectedFeatures}/
          {props.cell.getValue().totalFeatures}
        </SHTypography>
      ),
      enableColumnFilter: false,
      meta: {
        filterData: FeatureTemplateFilterOptions,
        sx: {
          width: "13%",
          padding: "0 5px !important",
        },
      },
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (props) => (
        <StatusBadge
          style={{ width: "88px", fontSize: 13 }}
          status={props.cell.getValue() as FeatureTemplateStatus}
        />
      ),
      meta: {
        filterData: RateCardFilterOptions,
        sx: {
          width: "10%",
          maxWidth: "120px",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "actionCol",
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (props) => renderItemActions(props.row.original),
      meta: {
        sx: {
          width: "5%",
        },
      },
    }),
  ];
  return cols;
};
